import { useFetch } from "../useFetch";
import type {
	CategoryResponse,
	CreateCategoryPayload,
	UpdateCategoryPayload,
} from "../../core/types/category";

// ============================================
// GET ALL CATEGORIES
// ============================================
export function useCategories() {
	return useFetch<CategoryResponse[]>({
		endpoint: "/category",
		method: "GET",
	});
}

// ============================================
// GET CATEGORY BY ID
// ============================================
export function useCategory(id: number) {
	return useFetch<CategoryResponse>({
		endpoint: `/category/${id}`,
		method: "GET",
	});
}

// ============================================
// CREATE CATEGORY
// ============================================
export function useCreateCategory() {
	const fetch = useFetch<CategoryResponse, CreateCategoryPayload>({
		endpoint: "/category",
		method: "POST",
		auto: false,
	});

	const create = (
		data: CreateCategoryPayload,
		files?: { picture_category?: File; icon_category?: File },
	) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, create };
}

// ============================================
// UPDATE CATEGORY
// ============================================
export function useUpdateCategory(id: number) {
	const fetch = useFetch<CategoryResponse, UpdateCategoryPayload>({
		endpoint: `/category/${id}`,
		method: "PATCH",
		auto: false,
	});

	const update = (
		data: UpdateCategoryPayload,
		files?: { picture_category?: File; icon_category?: File },
	) => {
		return fetch.execute({ body: data, files });
	};

	return { ...fetch, update };
}

// ============================================
// UPDATE CATEGORY AVAILABILITY
// ============================================
export function useUpdateCategoryAvailability(id: number) {
	// El body es pequeño y específico, lo podemos tipar inline o crear un type si se reutiliza
	const fetch = useFetch<CategoryResponse, { isActive: boolean }>({
		endpoint: `/category/${id}/availability`,
		method: "PATCH",
		auto: false,
	});

	const toggleAvailability = (isActive: boolean) => {
		return fetch.execute({ body: { isActive } });
	};

	return { ...fetch, toggleAvailability };
}

export function usePatchCategoryAvailability() {
	const fetch = useFetch<CategoryResponse, { isActive: boolean }>({
		endpoint: "/category",
		method: "PATCH",
		auto: false,
	});

	const setAvailability = (id: number, isActive: boolean) => {
		return fetch.execute({
			endpoint: `/category/${id}/availability`,
			body: { isActive },
		});
	};

	return { ...fetch, setAvailability };
}

// ============================================
// DELETE CATEGORY
// ============================================
export function useDeleteCategory() {
	// Generalmente delete devuelve un mensaje de éxito, no el objeto borrado
	const fetch = useFetch<{ message: string }>({
		endpoint: "/category",
		method: "DELETE",
		auto: false,
	});

	const remove = (id: number) => {
		return fetch.execute({ endpoint: `/category/${id}` });
	};

	return { ...fetch, remove };
}
